import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Save, 
  X, 
  Loader2,
  Target,
  TrendingUp,
  BarChart3,
  Leaf,
  Droplets,
  Zap,
  TreePine,
  Recycle,
  AlertCircle
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useMissionImpacts } from '../../hooks/useMissionImpacts';
import { useMissions } from '../../hooks/useMissions';
import { useImpactTypes } from '../../hooks/useImpactTypes';

interface MissionImpact {
  id: string;
  mission_id: string;
  impact_type_name: string;
  quantity_per_completion: number;
  unit: string;
  description: string;
}

export default function AdminMissionImpacts() {
  const [selectedMissionId, setSelectedMissionId] = useState<string>('');
  const [editingImpact, setEditingImpact] = useState<MissionImpact | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const { missionImpacts, loading, getMissionImpacts, addMissionImpact, updateMissionImpact, deleteMissionImpact } = useMissionImpacts();
  const { missions } = useMissions();
  const { impactTypes } = useImpactTypes();

  const activeMissions = missions.filter(m => m.active && !m.coming_soon);
  const selectedMission = activeMissions.find(m => m.id === selectedMissionId);
  const selectedMissionImpacts = selectedMissionId ? getMissionImpacts(selectedMissionId) : [];

  const emptyImpact: Omit<MissionImpact, 'id'> = {
    mission_id: selectedMissionId,
    impact_type_name: '',
    quantity_per_completion: 1,
    unit: '',
    description: ''
  };

  const handleSave = async (impact: MissionImpact | Omit<MissionImpact, 'id'>) => {
    setSaving(true);
    try {
      let result;
      if ('id' in impact) {
        result = await updateMissionImpact(impact.id, impact);
      } else {
        result = await addMissionImpact(impact);
      }

      if (!result.error) {
        setEditingImpact(null);
        setIsCreating(false);
      }
    } catch (error) {
      console.error('Error saving impact:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (impactId: string) => {
    if (!confirm('Are you sure you want to delete this impact mapping?')) return;

    try {
      await deleteMissionImpact(impactId);
    } catch (error) {
      console.error('Error deleting impact:', error);
    }
  };

  const getImpactTypeIcon = (typeName: string) => {
    const type = impactTypes.find(t => t.name === typeName);
    if (type) {
      const IconComponent = (LucideIcons as any)[type.icon] || Target;
      return <IconComponent className={`h-5 w-5 ${type.color}`} />;
    }
    return <Target className="h-5 w-5 text-gray-600" />;
  };

  const getImpactTypeColor = (typeName: string) => {
    const type = impactTypes.find(t => t.name === typeName);
    return type ? type.bg_color : 'bg-gray-100';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Mission Impact Mapping</h2>
        <p className="text-gray-600 mt-2">
          Configure what environmental impacts each mission contributes to user and global statistics
        </p>
      </div>

      {/* Mission Selection */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Target className="h-6 w-6 text-emerald-600" />
          <h3 className="text-lg font-semibold text-gray-900">Select Mission</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {activeMissions.map((mission) => {
            const IconComponent = (LucideIcons as any)[mission.icon] || Target;
            const impactCount = getMissionImpacts(mission.id).length;
            
            return (
              <button
                key={mission.id}
                onClick={() => setSelectedMissionId(mission.id)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  selectedMissionId === mission.id
                    ? 'border-emerald-500 bg-emerald-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="bg-emerald-100 p-2 rounded-lg">
                    <IconComponent className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900 mb-1">{mission.title}</h4>
                    <p className="text-sm text-gray-600 mb-2">{mission.category}</p>
                    <div className="flex items-center space-x-2">
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                        {impactCount} impact{impactCount !== 1 ? 's' : ''}
                      </span>
                      {mission.is_permanent && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          Permanent
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Mission Impacts */}
      {selectedMission && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <BarChart3 className="h-6 w-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Impact Configuration: {selectedMission.title}
                </h3>
                <p className="text-sm text-gray-600">{selectedMission.description}</p>
              </div>
            </div>
            <button
              onClick={() => setIsCreating(true)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Impact</span>
            </button>
          </div>

          {/* Impact List */}
          {selectedMissionImpacts.length > 0 ? (
            <div className="space-y-4">
              {selectedMissionImpacts.map((impact) => (
                <div key={impact.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`p-2 rounded-lg ${getImpactTypeColor(impact.impact_type_name)}`}>
                        {getImpactTypeIcon(impact.impact_type_name)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {impactTypes.find(t => t.name === impact.impact_type_name)?.label || impact.impact_type_name}
                        </h4>
                        <p className="text-sm text-gray-600">{impact.description}</p>
                        <div className="flex items-center space-x-4 mt-2">
                          <span className="text-sm font-medium text-emerald-600">
                            +{impact.quantity_per_completion} {impact.unit}
                          </span>
                          <span className="text-xs text-gray-500">per completion</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingImpact(impact)}
                        className="p-2 text-gray-600 hover:text-blue-600 rounded-lg transition-colors"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(impact.id)}
                        className="p-2 text-gray-600 hover:text-red-600 rounded-lg transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No Impact Mappings</h4>
              <p className="text-gray-600 mb-4">
                This mission doesn't have any environmental impact mappings configured yet.
              </p>
              <button
                onClick={() => setIsCreating(true)}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Add First Impact
              </button>
            </div>
          )}
        </div>
      )}

      {/* Impact Form Modal */}
      {(editingImpact || isCreating) && selectedMissionId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {editingImpact ? 'Edit Impact Mapping' : 'Add Impact Mapping'}
              </h3>
              <button
                onClick={() => {
                  setEditingImpact(null);
                  setIsCreating(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <ImpactForm
              impact={editingImpact || emptyImpact}
              impactTypes={impactTypes}
              onSave={handleSave}
              saving={saving}
            />
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <TrendingUp className="h-6 w-6 text-blue-600 mt-1" />
          <div>
            <h4 className="font-medium text-blue-900 mb-2">How Impact Tracking Works</h4>
            <div className="text-sm text-blue-700 space-y-2">
              <p>• Each time a user completes a mission, the configured impacts are automatically added to their personal statistics</p>
              <p>• Global impact statistics are also updated to reflect the collective environmental contribution</p>
              <p>• Permanent missions can be completed multiple times, multiplying the environmental impact</p>
              <p>• Impact quantities should reflect realistic environmental benefits (e.g., 0.5kg waste reduced per bottle recycled)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Impact Form Component
function ImpactForm({ 
  impact, 
  impactTypes,
  onSave, 
  saving 
}: { 
  impact: MissionImpact | Omit<MissionImpact, 'id'>;
  impactTypes: any[];
  onSave: (impact: MissionImpact | Omit<MissionImpact, 'id'>) => void;
  saving: boolean;
}) {
  const [formData, setFormData] = useState(impact);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const selectedImpactType = impactTypes.find(t => t.name === formData.impact_type_name);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Impact Type</label>
        <select
          value={formData.impact_type_name}
          onChange={(e) => {
            const selectedType = impactTypes.find(t => t.name === e.target.value);
            setFormData({ 
              ...formData, 
              impact_type_name: e.target.value,
              unit: selectedType?.unit || ''
            });
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          required
        >
          <option value="">Select impact type...</option>
          {impactTypes.filter(t => t.active).map(type => (
            <option key={type.id} value={type.name}>{type.label}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Quantity per Completion</label>
          <input
            type="number"
            step="0.1"
            value={formData.quantity_per_completion}
            onChange={(e) => setFormData({ ...formData, quantity_per_completion: parseFloat(e.target.value) || 0 })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="e.g., 0.5"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Unit</label>
          <input
            type="text"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder={selectedImpactType?.unit || "e.g., kg, L, kWh"}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          rows={3}
          placeholder="Describe how this mission contributes to this environmental impact..."
          required
        />
      </div>

      {/* Preview */}
      {formData.impact_type_name && formData.quantity_per_completion > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <h4 className="font-medium text-emerald-900 mb-2">Impact Preview</h4>
          <p className="text-sm text-emerald-700">
            Each time a user completes this mission, they will contribute{' '}
            <strong>+{formData.quantity_per_completion} {formData.unit}</strong> to their{' '}
            <strong>{selectedImpactType?.label}</strong> statistics.
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="submit"
          disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              <span>Save Impact</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}